/*================================================================
‘Copyright © 2020, Cheers Interactive Pvt Ltd.  All rights reserved.
      File Description            :  Company Advanced info UI for  Listing, edit, add and delete and API wirirng up 
 --------------------------------------------------------------------------------- 
    Creation Details 
    Date Created				: 05/Aug/2020 
    Author						: Anand Kumar 
================================================================ 
*/

import React, { useEffect, useState } from "react";
import { fetchSingleCompanyAdvanced } from "../../../../../middleware/services/companyApi";
import { useFormik } from "formik";
import * as Yup from "yup";
import { TextEditor } from "../../../../../app/components/CustomFormElements/syncFusionFormFields";
import { advancedValidationSchema } from "../../../../helper/validationSchema/companyValidationSchema";
import { actionError, showLoader, hideLoader } from "../../../../../middleware/actions/utilityAction";
import { useDispatch, useSelector } from "react-redux";
import ActionButtons from "./actionButtons";
import { ckEditorMinimalConfig } from "../../../../../config/ckEditorConfig";
import { setAdvanceTabDetails } from "../../../../../middleware/actions/companyAction";
import { TextField } from "../../../../components/CustomFormElements/syncFusionFormFields";
import PropTypes from "prop-types";
import { Accordion, AccordionSummary, AccordionDetails, Box, Button, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";

const initialState = {
  companyAbout: "",
  companyWhatsUnique: "",
  companyBusinessModel: "",
  companyFuturePlans: "",
};
const AdvancedUI = (props) => {
  const [expandedAccordions, setExpandedAccordions] = useState({
    aboutCompany: false,
    companyWhatsUnique: false,
    companyBusinessModel: false,
    companyFuturePlans: false,
  });
  const companyId = props.companyId;
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      ...initialState,
    },
    validationSchema: Yup.object().shape(advancedValidationSchema),
  });
  const commonState = useSelector((state) => state.companyState);
  const commonAdvance = commonState?.advanceDetails;

  const handleAccordionChange = (panel) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [panel]: !expandedAccordions[panel],
    }));
  };
  const sanitizeInput = (val) => {
    if (typeof val !== "string") return "";
    return val.replace(/<p>(&nbsp;|\s)*<\/p>/gi, "").trim();
  };

  const getCompanyDetails = () => {
    dispatch(showLoader());
    const params = {
      fields: ["companyAbout", "companyWhatsUnique", "companyBusinessModel", "companyFuturePlans"],
    };
    fetchSingleCompanyAdvanced(companyId, params)
      .then((response) => {
        props.setAdvancedFetched(true);
        formik.setValues({
          companyAbout: props.advancedFetched
            ? sanitizeInput(commonAdvance?.companyAbout) || ""
            : sanitizeInput(response.data.data.companyAbout) || "",
          companyBusinessModel: props.advancedFetched
            ? sanitizeInput(commonAdvance?.companyBusinessModel) || ""
            : sanitizeInput(response.data.data.companyBusinessModel) || "",
          companyWhatsUnique: props.advancedFetched
            ? sanitizeInput(commonAdvance?.companyWhatsUnique) || ""
            : sanitizeInput(response?.data?.data?.companyWhatsUnique) || "",
          companyFuturePlans: props.advancedFetched
            ? sanitizeInput(commonAdvance?.companyFuturePlans) || ""
            : sanitizeInput(response.data.data.companyFuturePlans) || "",
          id: commonAdvance?.id && commonAdvance?.id.length ? commonAdvance?.id : response.data.data.id,
        });
        dispatch(hideLoader());
      })
      .catch((err) => {
        dispatch(hideLoader());
        dispatch(actionError(err.data?.message || "Fails to get company advanced info"));
      });
  };
  useEffect(() => {
    companyId && getCompanyDetails();
  }, []);

  useEffect(() => {
    dispatch(setAdvanceTabDetails(formik.values));
  }, [formik.values]);
  return (
    <div className="gennx-content-wrapper pt-3">
      <div className="mt-0 mx-3">
        <div className="h-full flex flex-col w-auto border border-[#DBDBDD] overflow-y-auto">
          <Accordion
            sx={{
              backgroundColor: "white",
              margin: "0 !important",
              borderTop: "1px solid #e0e0e0",
              borderBottom: "none",
              borderLeft: "none",
              borderRight: "none",
              borderRadius: "0 !important",
              boxShadow: "none",
              "&.Mui-expanded": {
                margin: "0 !important",
                borderTop: "1px solid #e0e0e0",
              },
              "&:before": {
                display: "none",
              },
            }}
            expanded={expandedAccordions["aboutTheCompany"]}
            onChange={() => handleAccordionChange("aboutTheCompany")}
          >
            <AccordionSummary
              sx={{
                flexDirection: "row-reverse",
                "&.Mui-expanded": {
                  margin: "0",
                  minHeight: "48px",
                },
                "& .MuiAccordionSummary-content": {
                  flexGrow: 1,
                  margin: 0,
                  alignItems: "center",
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  marginLeft: "auto",
                },
                height: "60px",
              }}
              aria-controls="panel2bh-content"
              id="panel2bh-header"
            >
              <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                <CheckCircleIcon
                  sx={{
                    color: formik.values.companyAbout?.length > 0 ? "#0CB963" : "action.disabled",
                    fontSize: "32px",
                  }}
                />
                <Typography variant="h3" fontWeight="700" fontSize="16px" className="pl-2">
                  About The Company*
                </Typography>
              </Box>
              <Box className="">
                <Button variant="text" className="" onClick={() => handleAccordionChange("aboutTheCompany")}>
                  {expandedAccordions["aboutTheCompany"] ? (
                    <KeyboardArrowUpOutlinedIcon sx={{ color: "#000", fontSize: "30px" }} />
                  ) : (
                    <KeyboardArrowDownOutlinedIcon sx={{ color: "#000", fontSize: "30px" }} />
                  )}
                </Button>
              </Box>
            </AccordionSummary>
            {expandedAccordions["aboutTheCompany"] && (
              <AccordionDetails
                sx={{
                  "& .form-group": {
                    marginBottom: "0 !important",
                  },
                }}
              >
                <TextEditor
                  formik={formik}
                  maxLength={1000}
                  properties={{
                    id: "companyAbout",
                    name: "companyAbout",
                    activeClass: "editor",
                    config: {
                      ...ckEditorMinimalConfig,
                      editorplaceholder: "About The Company",
                      height: companyId ? "200px" : "206px",
                    },
                  }}
                />
              </AccordionDetails>
            )}
          </Accordion>

          <Accordion
            sx={{
              backgroundColor: "white",
              margin: "0 !important",
              borderTop: "1px solid #e0e0e0",
              borderBottom: "none",
              borderLeft: "none",
              borderRight: "none",
              borderRadius: "0 !important",
              boxShadow: "none",
              "&.Mui-expanded": {
                margin: "0 !important",
                borderTop: "1px solid #e0e0e0",
              },
              "&:before": {
                display: "none",
              },
            }}
            expanded={expandedAccordions["companyWhatsUnique"]}
            onChange={() => handleAccordionChange("companyWhatsUnique")}
          >
            <AccordionSummary
              sx={{
                flexDirection: "row-reverse",
                "&.Mui-expanded": {
                  margin: "0",
                  minHeight: "48px",
                },
                "& .MuiAccordionSummary-content": {
                  flexGrow: 1,
                  margin: 0,
                  alignItems: "center",
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  marginLeft: "auto",
                },
                height: "60px",
              }}
              aria-controls="panel2bh-content"
              id="panel2bh-header"
            >
              <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                <CheckCircleIcon
                  sx={{
                    color: formik.values?.companyWhatsUnique?.length > 0 ? "#0CB963" : "action.disabled",
                    fontSize: "32px",
                  }}
                />
                <Typography variant="h3" fontWeight="700" fontSize="16px" className="pl-2">
                  Company What's Unique/Tagline
                </Typography>
              </Box>
              <Box className="">
                <Button variant="text" className="!bg-transparent !text-[16px] !p-0" onClick={() => handleAccordionChange("companyWhatsUnique")}>
                  {expandedAccordions["companyWhatsUnique"] ? (
                    <KeyboardArrowUpOutlinedIcon sx={{ color: "#000", fontSize: "30px" }} />
                  ) : (
                    <KeyboardArrowDownOutlinedIcon sx={{ color: "#000", fontSize: "30px" }} />
                  )}
                </Button>
              </Box>
            </AccordionSummary>
            {expandedAccordions["companyWhatsUnique"] && (
              <AccordionDetails>
                <TextField
                  style={{ height: "100px" }}
                  formik={formik}
                  multiline={true}
                  numberCount={true}
                  rows={1}
                  maxLength={200}
                  name="companyWhatsUnique"
                  className="form-control"
                  id="companyWhatsUnique"
                  placeholder="Company What's Unique/Tagline"
                  characterLimitText={true}
                  hideInitial={true}
                  value={formik.values?.companyWhatsUnique || ""}
                />
              </AccordionDetails>
            )}
          </Accordion>

          <Accordion
            sx={{
              backgroundColor: "white",
              margin: "0 !important",
              borderTop: "1px solid #e0e0e0",
              borderBottom: "none",
              borderLeft: "none",
              borderRight: "none",
              borderRadius: "0 !important",
              boxShadow: "none",
              "&.Mui-expanded": {
                margin: "0 !important",
                borderTop: "1px solid #e0e0e0",
              },
              "&:before": {
                display: "none",
              },
            }}
            expanded={expandedAccordions["companyBusinessModel"]}
            onChange={() => handleAccordionChange("companyBusinessModel")}
          >
            <AccordionSummary
              sx={{
                flexDirection: "row-reverse",
                "&.Mui-expanded": {
                  margin: "0",
                  minHeight: "48px",
                },
                "& .MuiAccordionSummary-content": {
                  flexGrow: 1,
                  margin: 0,
                  alignItems: "center",
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  marginLeft: "auto",
                },
                height: "60px",
              }}
              aria-controls="panel2bh-content"
              id="panel2bh-header"
            >
              <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                <CheckCircleIcon
                  sx={{
                    color: formik.values.companyBusinessModel?.length > 0 ? "#0CB963" : "action.disabled",
                    fontSize: "32px",
                  }}
                />
                <Typography variant="h3" fontWeight="700" fontSize="16px" className="pl-2">
                  Company Business Model
                </Typography>
              </Box>
              <Box className="">
                <Button variant="text" className="!bg-transparent !text-[16px] !p-0" onClick={() => handleAccordionChange("companyBusinessModel")}>
                  {expandedAccordions["companyBusinessModel"] ? (
                    <KeyboardArrowUpOutlinedIcon sx={{ color: "#000", fontSize: "30px" }} />
                  ) : (
                    <KeyboardArrowDownOutlinedIcon sx={{ color: "#000", fontSize: "30px" }} />
                  )}
                </Button>
              </Box>
            </AccordionSummary>
            {expandedAccordions["companyBusinessModel"] && (
              <AccordionDetails
                sx={{
                  "& .form-group": {
                    marginBottom: "0 !important",
                  },
                }}
              >
                <TextEditor
                  formik={formik}
                  maxLength={1000}
                  properties={{
                    id: "companyBusinessModel",
                    name: "companyBusinessModel",
                    activeClass: "editor",
                    config: {
                      ...ckEditorMinimalConfig,
                      editorplaceholder: "Company Business Model",
                      height: companyId ? "200px" : "206px",
                    },
                  }}
                />
              </AccordionDetails>
            )}
          </Accordion>

          <Accordion
            sx={{
              backgroundColor: "white",
              margin: "0 !important",
              borderTop: "1px solid #e0e0e0",
              borderBottom: "none",
              borderLeft: "none",
              borderRight: "none",
              borderRadius: "0 !important",
              boxShadow: "none",
              "&.Mui-expanded": {
                margin: "0 !important",
                borderTop: "1px solid #e0e0e0",
              },
              "&:before": {
                display: "none",
              },
            }}
            expanded={expandedAccordions["companyFuturePlans"]}
            onChange={() => handleAccordionChange("companyFuturePlans")}
          >
            <AccordionSummary
              sx={{
                flexDirection: "row-reverse",
                "&.Mui-expanded": {
                  margin: "0",
                  minHeight: "48px",
                },
                "& .MuiAccordionSummary-content": {
                  flexGrow: 1,
                  margin: 0,
                  alignItems: "center",
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  marginLeft: "auto",
                },
                height: "60px",
              }}
              aria-controls="panel2bh-content"
              id="panel2bh-header"
            >
              <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                <CheckCircleIcon
                  sx={{
                    color: formik.values.companyFuturePlans?.length > 0 ? "#0CB963" : "action.disabled",
                    fontSize: "32px",
                  }}
                />
                <Typography variant="h3" fontWeight="700" fontSize="16px" className="pl-2">
                  Future Plans
                </Typography>
              </Box>
              <Box className="">
                <Button variant="text" className="!bg-transparent !text-[16px] !p-0" onClick={() => handleAccordionChange("companyFuturePlans")}>
                  {expandedAccordions["companyFuturePlans"] ? (
                    <KeyboardArrowUpOutlinedIcon sx={{ color: "#000", fontSize: "30px" }} />
                  ) : (
                    <KeyboardArrowDownOutlinedIcon sx={{ color: "#000", fontSize: "30px" }} />
                  )}
                </Button>
              </Box>
            </AccordionSummary>
            {expandedAccordions["companyFuturePlans"] && (
              <AccordionDetails
                sx={{
                  "& .form-group": {
                    marginBottom: "0 !important",
                  },
                }}
              >
                <TextEditor
                  formik={formik}
                  maxLength={1000}
                  properties={{
                    id: "companyFuturePlans",
                    name: "companyFuturePlans",
                    activeClass: "editor",
                    config: {
                      ...ckEditorMinimalConfig,
                      editorplaceholder: "Future Plans",
                      height: companyId ? "200px" : "206px",
                    },
                  }}
                />
              </AccordionDetails>
            )}
          </Accordion>
        </div>
      </div>
      <ActionButtons
        accessRights={props.accessRights}
        selectedIndex={props.selectedIndex}
        nextPath={companyId ? `/company/${companyId}/edit#basic` : true}
        previousPath={`/company/${companyId}/edit#basic`}
        handleCommonSave={(isSubmit) => {
          formik.validateForm().then((errors) => {
            if (Object.keys(errors).length === 0) {
              props.handleCommonSave(isSubmit);
              //dispatch(setAdvanceTabDetails(formik.values));
            } else {
              console.log("errors", errors);
            }
          });
        }}
        setCurrentTab={(selectedIndex) => {
          props.setCurrentTab(selectedIndex);
        }}
        back={props.path === "/processing/company/:companyId/edit" ? "/processing/company" : "/company"}
        companyId={companyId}
        formik={formik}
        hashArr={props.hashArr}
        getCompanyDetails={getCompanyDetails}
      />
    </div>
  );
};
AdvancedUI.propTypes = {
  accessRights: PropTypes.arrayOf(PropTypes.string),
  advancedFetched: PropTypes.bool,
  setAdvancedFetched: PropTypes.func,
  selectedIndex: PropTypes.number,
  setCurrentTab: PropTypes.func,
  handleCommonSave: PropTypes.func,
  companyId: PropTypes.string,
  path: PropTypes.string,
  hashArr: PropTypes.array,
};
export default AdvancedUI;
